






































import {
  watch,
  defineComponent,
  onMounted,
  ref,
  Ref,
} from '@vue/composition-api';
import { debounce } from 'lodash';
import { useRouter, useFormat } from '@ideavivat/vivat-2';

import Bubble from '@/components/Chat/Bubble.vue';
import useEcho from '@/useEcho';
import useAuth from '@/useAuth';

import CommonModalWithPreset from '@/components/CommonModalWithPreset.vue';

import {
  getChatMessages,
  submitMessageToSession,
  deleteChatMessageInSession,
} from '@/api/index';

interface Message {
  id: number;
  message: string;
  sender: {
    id: number;
    name: string;
    avatar: string;
  };
  time: string;
  isSelf: boolean;
}

const ChatBox = defineComponent({
  components: {
    Bubble,
    CommonModalWithPreset,
  },
  setup(props: any, { emit }) {
    const { route } = useRouter();
    const { pusher } = useEcho();
    const { getMe } = useAuth();
    const me = ref({
      id: -1,
    });

    const loading = ref(false);
    const pagination = ref({
      // eslint-disable-next-line
      current_page: 1,
      total: 0,
    });
    const page = ref(1);
    const sort = ref({ field: 'created_at', order: 'DESC' });

    function mapAPIDataToQuestion(message: any) {
      const time = useFormat.date(message.created_at).displayShortThaiDateTime();
      return {
        id: message.id,
        message: message.message,
        sender: {
          id: message.user?.id ? message.user.id : -1,
          name: message.user?.name ? message.user.name : 'ผู้ใช้นี้ถูกลบออกจากระบบ',
          admin: message.user?.admin ? message.user.admin : 'unknown',
        },
        time,
        isSelf: message.user?.id === (me.value).id,
      };
    }

    const messages: Ref<Message[]> = ref([]);
    const callGetChatMessages = debounce(async (reset = false) => {
      loading.value = true;
      const { data, ...paginatorData } = await getChatMessages(
        route.value.params.liveId,
        page.value,
        30,
        sort.value,
      );

      if (messages.value.length > 0 && reset === false) {
        const temporaryMessages = data?.map(mapAPIDataToQuestion).sort((a: any, b: any) => a.id - b.id) || [];
        messages.value = [...temporaryMessages, ...messages.value];
      } else {
        page.value = 1;
        messages.value = data?.map(mapAPIDataToQuestion).sort((a: any, b: any) => a.id - b.id) || [];
      }
      pagination.value = paginatorData;
      loading.value = false;
    }, 1000);

    const textMessage = ref('');
    const readyToSubmitMessage = ref(true);

    const messagesArea: Ref<HTMLElement | null> = ref(null);
    async function scrollMessagesAreaHandler({ target: { scrollTop, clientHeight, scrollHeight } }: any) {
      if (scrollTop === 0) {
        page.value += 1;
        await callGetChatMessages();
      }
    }

    function scrollToBottom(smooth = true) {
      (messagesArea.value as HTMLElement).scrollTo({
        top: (messagesArea.value as HTMLElement).scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }

    async function submit() {
      if (textMessage.value === '') return '';
      await submitMessageToSession(route.value.params.liveId, textMessage.value);
      textMessage.value = '';
      return '';
    }

    async function deleteChatMessage(id: number | string) {
      await deleteChatMessageInSession(id);
    }

    async function processReceiveChatEvent(data: any) {
      const time = (new Date(data.created_at)).toTimeString().substring(0, 5);

      emit('hasUpdate');
      const message = {
        id: data.id,
        message: data.message,
        sender: {
          id: data.user.id,
          name: data.user.name,
          avatar: data.user.avatar_path,
          admin: data.user.admin,
        },
        time,
        isSelf: data.user.id === (me.value as any).id,
      };
      if (messages.value.length > 300) messages.value.splice(0, 30);
      if (messages.value.length === 0 || messages.value[messages.value.length - 1].id !== message.id) await messages.value.push(message);
      await scrollToBottom();
    }

    function processDeleteChatEvent(data: any) {
      if (messages.value.length > 0) {
        const index = messages.value.findIndex((message: Message) => message.id === data.id);
        if (index > -1) {
          messages.value.splice(index, 1);
        }
      }
    }

    function subscribeToPusher() {
      if (pusher) {
        const channel = pusher.subscribe(`session.${route.value.params.liveId}`);
        console.log('📌 Pusher: subscribe success', `session.${route.value.params.liveId}`);
        channel.bind('ChatMessageCreated', async (data: any) => {
          await processReceiveChatEvent(data.chatMessage);
        });
        channel.bind('ChatMessageDeleted', (data: any) => {
          if (data.chat === null) messages.value = [];
          else processDeleteChatEvent(data.chat);
        });
      }
    }
    function unsubscribeFromPusher(liveId: string) {
      if (pusher) {
        pusher.unsubscribe(`session.${liveId}`);
      }
    }

    onMounted(async () => {
      me.value = await getMe();

      await callGetChatMessages(true);
      await scrollToBottom(false);
      subscribeToPusher();
    });

    function checkIsMessageHaveSameSender(index: number) {
      if (index === 0) return false;
      if (
        (messages.value)[index].sender.id === (messages.value)[index - 1].sender.id
        && (messages.value)[index].time === (messages.value)[index - 1].time
      ) return true;
      return false;
    }

    watch(() => route.value.params.liveId, async (newVal, oldVal) => {
      if (newVal !== oldVal) {
        page.value = 1;
        await callGetChatMessages(true);
        subscribeToPusher();
        unsubscribeFromPusher(oldVal);
      }
    });

    return {
      messages,
      textMessage,
      submit,
      me,
      readyToSubmitMessage,
      pusher,
      deleteChatMessage,

      messagesArea,
      scrollMessagesAreaHandler,

      checkIsMessageHaveSameSender,
    };
  },
});

export default ChatBox;
